import { useLocalSessionId } from '@daily-co/daily-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isDebugUIEnabled } from '../../config/debug';
import { useEndSessionDebug } from '../../context/EndSessionDebugContext';
import useParticipantsWithHost from '../../hooks/useParticipantsWithHost';
import useWSMessageSender from '../../hooks/useWSMessageSender';
import useWSTurnState, { TURN_ACTIONS } from '../../hooks/useWSTurnState';
import ParticipantCamera from './ParticipantCamera';
import './SlidesCommon.css';

const CELEBRATION_DURATION = 3000;
const VISUAL_TIMER_START = 30;

const containerStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 32,
  zIndex: 1,
};

const camerasRowStyle = {
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-evenly',
};

const bottomSectionStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 24,
};

const titleSpanStyle = {
  fontSize: 'clamp(2.8rem, 2.8vw, 4.2rem)',
  position: 'absolute',
  width: '100%',
  textAlign: 'center',
};

const titleMotionProps = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const guaraImageStyle = {
  position: 'absolute',
  right: '5%',
  bottom: '-20%',
  zIndex: 0,
  maxWidth: '260px',
  height: 'auto',
};

const readyBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '12px 28px',
  fontSize: '17px',
  fontFamily: 'League Spartan',
  fontWeight: 700,
  color: '#55B4BE',
  backgroundColor: '#fff',
  border: 'none',
  borderRadius: '100px',
  cursor: 'pointer',
};

const countdownTextStyle = { opacity: 0.75, textAlign: 'center' };

// Wrapper que mantém o espaço do botão mesmo após ele sumir
const btnSlotStyle = {
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export default function ContentGoodbye() {
  // --- participantes ---
  const { participantA, participantB } = useParticipantsWithHost();
  const localSessionId = useLocalSessionId();

  // --- estado da sessão ---
  const { goodbyeOks } = useWSTurnState();
  const { sendTurnAction } = useWSMessageSender();
  const { debugGoodbyeDone, setDebugGoodbyeDone } = useEndSessionDebug();

  // --- estado visual ---
  const [showFarewell, setShowFarewell] = useState(false);
  const [countdown, setCountdown] = useState(VISUAL_TIMER_START);

  useEffect(() => {
    const t = setTimeout(() => setShowFarewell(true), CELEBRATION_DURATION);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!showFarewell || countdown <= 0) return;
    const interval = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [showFarewell, countdown]);

  // --- lógica ---
  const isCurrentUserDone = useMemo(() => {
    if (isDebugUIEnabled) return debugGoodbyeDone;
    return localSessionId && Array.isArray(goodbyeOks) && goodbyeOks.includes(localSessionId);
  }, [localSessionId, goodbyeOks, debugGoodbyeDone]);

  const handleReadyClick = useCallback(() => {
    if (isCurrentUserDone) return;
    if (isDebugUIEnabled) {
      setDebugGoodbyeDone(true);
    } else {
      sendTurnAction({ action: TURN_ACTIONS.GoodbyeOk, participantId: localSessionId });
    }
  }, [isCurrentUserDone, localSessionId, sendTurnAction, setDebugGoodbyeDone]);

  return (
    <motion.div
      className="slide-container"
      style={{ position: 'relative' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: 'easeInOut' }}
    >
      <img src="/images/guara-1.png" alt="Guará" style={guaraImageStyle} />

      <div style={containerStyle}>
        <div style={{ position: 'relative', width: '100%', height: 'clamp(3.4rem, 3.4vw, 5.1rem)' }}>
          <AnimatePresence mode="wait">
            {showFarewell ? (
              <motion.span key="farewell" className="white-title" style={titleSpanStyle} {...titleMotionProps}>
                Time to say bye!
              </motion.span>
            ) : (
              <motion.span key="celebrate" className="white-title" style={titleSpanStyle} {...titleMotionProps}>
                Session complete! 🎉
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        <div style={camerasRowStyle}>
          {participantA && <ParticipantCamera participant={participantA} />}
          {participantB && <ParticipantCamera participant={participantB} />}
        </div>

        <motion.div
          style={bottomSectionStyle}
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: showFarewell ? 1 : 0, y: showFarewell ? 0 : -16 }}
          transition={{ duration: 0.4, ease: 'easeOut', delay: 0.5 }}
        >
          <span className="white-subtitle" style={countdownTextStyle}>
            Moving to individual feedback in {countdown > 0 ? countdown : 0} seconds.
          </span>

          <div style={btnSlotStyle}>
            <AnimatePresence>
              {!isCurrentUserDone && (
                <motion.button
                  type="button"
                  onClick={handleReadyClick}
                  style={readyBtnStyle}
                  exit={{ opacity: 0, x: 60 }}
                  transition={{ duration: 0.35, ease: 'easeIn' }}
                >
                  I said my goodbyes →
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
